import type { ProjectMembership } from "schemas/generated/projectMembership";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { skipToken } from "@tanstack/react-query";
import { Avatar } from "@v1/components/common/designSystem/components/avatar/Avatar";
import { Separator } from "@v1/components/common/designSystem/components/separator/Separator";
import { Skeleton } from "@v1/components/common/designSystem/components/skeleton/Skeleton";
import InputComponent from "@v1/components/common/designSystem/Input";
import ErrorMessage from "@v1/components/common/ErrorMessage/ErrorMessage";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import { useHasWorkspaceMembership } from "@v1/hooks/useHasWorkspaceMembership";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useIsWorkspaceMember } from "@v2/hooks/useIsWorkspaceMember";
import { useModal } from "@v2/store/modalStore";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { ChevronRight, Search, Users, X } from "lucide-react";
import ContentLoader from "react-content-loader";
import { Controller, useForm } from "react-hook-form";
import { generatePath, Link } from "react-router-dom";
import { getFromRecordOrNull } from "replo-runtime/shared/utils/optional";
import { z } from "zod";

/**
 * Emails which we'll reject instead of adding them to the project if the user types them in
 */
const blockedEmailConfig: Record<string, { title: string; subtitle: string }> =
  {
    "support@replo.app": {
      title: "support@replo.app doesn't need to be added as a collaborator.",
      subtitle:
        "Don't worry, Replo Support will be able to access this project to help.",
    },
  };

const AddCollaboratorSchema = z.object({
  email: z.email().min(1, "Please enter a valid email address"),
});

export const ProjectMembershipModal = () => {
  const modal = useModal();
  const {
    handleSubmit,
    formState: { errors, isLoading: isLoadingForm },
    reset,
    control,
  } = useForm<{ email: string }>({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(AddCollaboratorSchema),
  });
  const projectId = useCurrentProjectId();
  const { isAuthenticated } = useCurrentUser();
  const errorToast = useV1ErrorToast();
  const { data, isLoading } = projectRouter.membership.list.useQuery(
    projectId && isAuthenticated ? { projectId } : skipToken,
  );

  const {
    workspace,
    isLoading: isLoadingWorkspace,
    workspaceId,
  } = useCurrentWorkspaceContext();

  const isWorkspaceMember = useIsWorkspaceMember(workspaceId);

  const { workspaceMembershipsIsLoading, workspaceMembershipsData } =
    useHasWorkspaceMembership();

  const workspaceName = workspace?.name;

  const { mutateAsync: inviteProjectMembers, isPending: isInvitingProject } =
    projectRouter.membership.inviteMembers.useMutation({
      onSuccess: async ({ projectMemberships }, variables) => {
        void workspaceUtils.getWorkspaceAndProjectMembers.invalidate();
        void projectUtils.membership.list.invalidate({
          projectId: variables.projectId,
        });
        const first = projectMemberships[0];
        if (!first) {
          return;
        }
        if (!isWorkspaceMember) {
          return;
        }
        if (!workspaceId || !workspaceName) {
          return;
        }

        modal.closeModal({ type: "projectMembershipModal" });
        modal.openModal({
          type: "workspaceMembershipModal",
          props: {
            workspaceId,
            workspaceName,
            email: first.project.user.email,
          },
        });
      },
    });

  const createProjectMembership = async ({ email }: { email: string }) => {
    if (!projectId) {
      return;
    }

    const blockedConfig = getFromRecordOrNull(blockedEmailConfig, email);
    if (blockedConfig) {
      errorToast(
        blockedConfig.title,
        blockedConfig.subtitle,
        "error.project.membership.blocked",
        {
          blockedConfigTitle: blockedConfig.title,
          blockedConfigSubtitle: blockedConfig.subtitle,
        },
      );
      return;
    }
    await inviteProjectMembers({ projectId, emails: [email] });
    reset();
  };
  const areProjectOrWorkspaceMembershipsLoading =
    isLoading || workspaceMembershipsIsLoading || isLoadingWorkspace;
  const shouldShowWorkspaceMembershipsWarn =
    !areProjectOrWorkspaceMembershipsLoading && workspaceId;

  const projectMembershipsThatAreNotWorkspaceMembers =
    data?.projectMemberships.filter((projectMembership) => {
      return !workspaceMembershipsData?.workspaceMemberships.some(
        (workspaceMembership) => {
          return (
            workspaceMembership.user.id === projectMembership.project.user.id
          );
        },
      );
    });

  return (
    <form
      onSubmit={(data) => {
        void handleSubmit(createProjectMembership)(data);
      }}
    >
      <Modal
        title="Invite to Project"
        onOpenChange={(open) => {
          if (!open) {
            modal.closeModal({ type: "projectMembershipModal" });
          }
        }}
        isOpen
        size="base"
      >
        <div className="flex flex-col gap-1">
          <div className="flex w-full flex-row gap-x-2">
            <Controller
              render={({ field }) => (
                <InputComponent
                  {...field}
                  autoComplete="off"
                  placeholder="Collaborator Email"
                  startEnhancer={<Search size={14} className="text-muted" />}
                />
              )}
              control={control}
              name="email"
            />
            <DeprecatedButton
              variant="primary"
              type="submit"
              size="sm"
              disabled={isLoading || isLoadingForm || isInvitingProject}
              isLoading={isInvitingProject}
            >
              Invite
            </DeprecatedButton>
          </div>
          <ErrorMessage error={errors["email"]?.message} />
        </div>
        {areProjectOrWorkspaceMembershipsLoading && <InvitationModalSkeleton />}
        {shouldShowWorkspaceMembershipsWarn && (
          <Link
            to={generatePath(routes.shops.settings.workspace.members, {
              workspaceId,
              projectId,
            })}
            className="flex flex-row items-center gap-2"
          >
            <Users size={14} />
            <p className="text-sm">
              Everyone in {workspaceName ?? "this workspace"} has edit access
            </p>
            <div className="flex flex-1 flex-row justify-end">
              <ChevronRight size={14} />
            </div>
          </Link>
        )}
        <Separator />
        <div className="text-xs">More People with Access</div>
        <div className="flex max-h-72 flex-col gap-y-2 overflow-y-scroll">
          {isLoading && <ProjectMembershipRowSkeletonLoader />}
          {!isLoading &&
            projectMembershipsThatAreNotWorkspaceMembers?.map(
              (project: ProjectMembership) => {
                return (
                  <ProjectMembershipRow
                    projectMembership={project}
                    key={project.id}
                  />
                );
              },
            )}
          {((!isLoading && !projectMembershipsThatAreNotWorkspaceMembers) ||
            projectMembershipsThatAreNotWorkspaceMembers?.length === 0) && (
            <p className="text-muted text-xs">
              No members have single-project access.
            </p>
          )}
        </div>
      </Modal>
    </form>
  );
};

type ProjectMembershipRowProps = {
  projectMembership: ProjectMembership;
};

const ProjectMembershipRow = ({
  projectMembership,
}: ProjectMembershipRowProps) => {
  const projectId = projectMembership.project.id;
  const { mutateAsync: deleteProjectMembership } =
    projectRouter.membership.delete.useMutation({
      onSuccess: async () => {
        void projectUtils.membership.list.invalidate({ projectId });
      },
    });
  const { user: currentUser } = useCurrentUser();

  const { user } = projectMembership.project;
  const removeLabel = `Remove ${
    user.name && user.name.length > 0 ? user.name : user.email
  }`;

  const isCurrentUser = currentUser?.id === user.id;

  return (
    <div className="align-center flex flex-row items-center justify-center">
      <div className="flex grow flex-row items-center gap-x-3">
        <Avatar name={user.name} className="h-7 w-7" />
        <div className="flex flex-col items-start text-xs">
          <div className="text-default text-xs">{user.name}</div>
          <div className="text-muted text-xs">{user.email}</div>
        </div>
      </div>
      {!isCurrentUser && (
        <DeprecatedIconButton
          UNSAFE_className="col-span-1 justify-self-end"
          icon={<X size={16} />}
          tooltipText={removeLabel}
          variant="tertiary"
          aria-label={removeLabel}
          onClick={() => {
            void deleteProjectMembership({
              id: projectMembership.id,
              projectId: projectMembership.project.id,
            });
          }}
        />
      )}
    </div>
  );
};

const ProjectMembershipRowSkeletonLoader = () => {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <ContentLoader
          key={i}
          speed={2}
          width={302}
          height={40}
          viewBox="0 0 302 40"
          backgroundColor="#f1f5f9"
          foregroundColor="#e2e8f0"
        >
          <circle cx="20" cy="20" r="18" />
          <rect x="48" y="4" rx="4" ry="4" width="170" height="12" />
          <rect x="48" y="22" rx="4" ry="4" width="170" height="12" />
        </ContentLoader>
      ))}
    </>
  );
};

const InvitationModalSkeleton = () => (
  <div className="h-[30px] w-[302px]">
    <Skeleton className="h-[28px] w-[302px]" />
  </div>
);
